import { WsClient, type ConnectionStatus } from '@/protocol/wsClient';
import {
  getOrCreateIdentity,
  getLastEventId,
  detectStorageMode,
} from '@/protocol/identity';
import { clearLastRoom, saveLastRoom } from '@/protocol/roomStorage';
import type { Indicator, ServerMessage } from '@/protocol/types';
import { useSessionStore } from '@/store/sessionStore';
import { navigate } from '@/hooks/useRoute';

let client: WsClient | null = null;
let currentRoom: string | null = null;

function resolveWsUrl(): string {
  const envUrl = import.meta.env.VITE_WS_URL;
  if (envUrl) return envUrl;
  if (typeof window === 'undefined') return 'ws://localhost:8787/ws';
  const isHttps = window.location.protocol === 'https:';
  const proto = isHttps ? 'wss:' : 'ws:';
  // 默认走同域 /ws；本地开发 fallback 到 8787
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return `ws://${window.location.hostname}:8787/ws`;
  }
  return `${proto}//pigwords-api.kerosdev.com/ws`;
}

/**
 * 初始化 / 切换房间。
 * - 同 roomCode 已连接 → 直接返回 singleton
 * - 异 roomCode → 关旧连接、建新连接、切 store.roomCode
 */
export function initSession(roomCode: string): WsClient {
  if (client && currentRoom === roomCode) return client;
  if (client && currentRoom !== roomCode) {
    try {
      client.close();
    } catch {
      // ignore
    }
    client = null;
  }
  currentRoom = roomCode;
  const store = useSessionStore.getState();
  const identity = getOrCreateIdentity();
  store.setStorageMode(detectStorageMode());
  store.setInitialIdentity(identity.token, identity.nickname);
  store.setRoomCode(roomCode);
  saveLastRoom(roomCode);

  client = new WsClient({
    url: resolveWsUrl(),
    token: identity.token,
    roomCode,
    getLastEventId,
    onMessage: (msg: ServerMessage) => {
      useSessionStore.getState().applyServerMessage(msg);
    },
    onStatusChange: (status: ConnectionStatus) => {
      useSessionStore.getState().setConnectionStatus(status);
    },
    onRoomFatal: ({ code }) => {
      // 房间相关致命错误 → 清房间号 + 通知 + 跳回 /
      const msgMap: Record<string, string> = {
        INVALID_ROOM: '房间号不合法',
        ROOM_NOT_FOUND: '房间不存在',
        ROOM_FULL: '房间已满',
        ROOM_CLOSED: '房间已关闭',
      };
      const message = msgMap[code] ?? '房间不可用';
      const s = useSessionStore.getState();
      s.setRoomCode(null);
      // 复用 lastError toast 通道
      (s as unknown as { lastError: unknown }).lastError = null;
      useSessionStore.setState({
        lastError: { code, message, ts: Date.now() },
      });
      clearLastRoom();
      currentRoom = null;
      client = null;
      navigate('/');
    },
  });
  client.connect();
  return client;
}

/** 退出当前房间：关连接 + 清 localStorage + 跳 / */
export function leaveRoom(): void {
  if (client) {
    try {
      client.close();
    } catch {
      // ignore
    }
    client = null;
  }
  currentRoom = null;
  clearLastRoom();
  useSessionStore.getState().setRoomCode(null);
  useSessionStore.getState().__reset();
  navigate('/');
}

export function getWsClient(): WsClient | null {
  return client;
}

/** 测试用：重置 singleton */
export function __resetWsForTest(): void {
  if (client) {
    try {
      client.close();
    } catch {
      // ignore
    }
  }
  client = null;
  currentRoom = null;
}

// ───────── 命令包装 ─────────

function send<T = unknown>(type: Parameters<WsClient['send']>[0], payload: T): string | null {
  if (!client) return null;
  return client.send(type, payload);
}

export const cmd = {
  setNickname(nickname: string) {
    return send('SET_NICKNAME', { nickname });
  },
  /** v1 新事件：任意玩家发起开局，服务端直接进 role_reveal（无同意窗口） */
  startGame() {
    return send('START_GAME', {});
  },
  /** v2.1 hotfix：村长确认候选词（按 id） */
  confirmWord(wordId: number) {
    return send('CONFIRM_WORD', { wordId });
  },
  /** v1：村长重抽候选词 */
  rerollCandidates() {
    return send('REROLL_CANDIDATES', {});
  },
  /** v1：swipe 占位（真手势 D3 再接入） */
  swipe(payload: { indicator?: Indicator; targetToken?: string } = {}) {
    return send('SWIPE', payload);
  },
  /** v2.1 hotfix：村长结束对局时显式传 result */
  endGame(result: 'good_win' | 'wolf_win') {
    return send('END_GAME', { result });
  },
  /** v1：看完身份 ack */
  roleRevealAck() {
    return send('ROLE_REVEAL_ACK', {});
  },
  returnLobby() {
    return send('RETURN_LOBBY', {});
  },

  // ---- 以下为 v0 废弃事件（SDD-WS v1 §4.99）：role_reveal/word_pick/playing/end 完整重写在 D3/D4
  // 临时保留函数签名使现有调用点能编译，但内部 no-op，避免发出 v0 事件触发 EVENT_DEPRECATED 横幅。
  ready() {
    deprecatedNoop('READY');
    return null;
  },
  unready() {
    deprecatedNoop('UNREADY');
    return null;
  },
  /** @deprecated v1 已改名 START_GAME，这里为兼容历史调用点转发到 startGame */
  startRequest() {
    return send('START_GAME', {});
  },
  startVote(_agree: boolean) {
    deprecatedNoop('START_VOTE');
    return null;
  },
  cancelStart() {
    deprecatedNoop('CANCEL_START');
    return null;
  },
  longPressBegin(_targetToken: string) {
    deprecatedNoop('CHIEF_LONG_PRESS_BEGIN');
    return null;
  },
  longPressCancel(_targetToken: string) {
    deprecatedNoop('CHIEF_LONG_PRESS_CANCEL');
    return null;
  },
  longPressCommit(_targetToken: string, _indicator: Indicator) {
    deprecatedNoop('CHIEF_LONG_PRESS_COMMIT');
    return null;
  },
  chiefDeclareCorrect() {
    deprecatedNoop('CHIEF_DECLARE_CORRECT');
    return null;
  },
  wolfAccuse(_targetToken: string) {
    deprecatedNoop('WOLF_ACCUSE');
    return null;
  },
};

function deprecatedNoop(event: string): void {
  if (typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.warn(
      `[pigwords] v0 事件 ${event} 在 v1 下已废弃，本次发送被拦截。该阶段 UI 在 D3/D4 重写。`,
    );
  }
}
